<template>
    <div class="row">
        <div class="col-12 note-head">
            <div class="note-title">
                <h4>{{ note.name }}</h4>
            </div>
            <div class="note-btn d-flex align-items-center" v-if="!note.isHideAddBtn">
                <button @click="onOpenModal(note, index)" type="button" class="btn btn-outline-primary btn-sm">
                    <i v-if="!salesOrderId" class="fas fa-plus"></i>
                    <i v-else class="fas fa-pen"></i>
                </button>
            </div>
        </div>

        <div class="col-12"><hr class="mt-0"></div>
        <div class="col-12 note-body">
            <div class="table-container">
                <table class="table treatment-table mb-0">
                    <thead>
                        <tr>
                            <th class="col-action"></th>
                            <th class="col-item">Item Details</th>
                            <th class="col-doctor">Doctor</th>
                            <th class="col-schedule">Schedule</th>
                            <th class="col-dentition">Dentition Plan</th>
                            <th class="col-bill-dent">Bill Dentition</th>
                            <th class="col-amount">Bill Amount</th>
                            <th class="col-status">Status</th>
                        </tr>
                    </thead>

                    <tbody ref="tableRef">
                        <template v-for="(item, noteIndex) in note.selectedNotes" :key="noteIndex">
                            <tr :class="{ 'row-highlighted': generalId == item.id }">
                                <td class="col-action">
                                    <button
                                        v-if="item.id"
                                        @click="onOpenTaskModal(item)"
                                        class="btn btn-outline-primary btn-sm task-btn"
                                    >
                                        <i class="fas fa-plus"></i> Task
                                    </button>
                                </td>
                                <td class="col-item fw-medium">{{ item.name }}</td>
                                <td class="col-doctor">{{ item.service_resource ? item.service_resource.name : '' }}</td>
                                <td class="col-schedule text-nowrap">
                                    {{ item.schedule_date ? formatDate(new Date(item.schedule_date)) : '' }}
                                </td>
                                <td class="col-dentition">{{ item.dentition ? item.dentition.join(', ') : '' }}</td>
                                <td class="col-bill-dent">{{ getDentitionPertains(item.dentition_pertains) }}</td>
                                <td class="col-amount text-end fw-medium">{{ item.total }}</td>
                                <td class="col-status" v-html="getStatus(item.status)"></td>
                            </tr>
                            <ServiceTask 
                                v-if="item.items && item.items.length > 0" 
                                :childItems="item.items"
                                :colspan="8"
                                @onOpenEditModal="(childItem, childIndex) => onOpenEditModal(childItem, noteIndex, childIndex, item)"
                            />
                        </template>

                        <tr v-if="!note.selectedNotes || !note.selectedNotes.length">
                            <td colspan="8" class="text-center text-muted py-4">No data</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <AddTreatmentPlan
            :selectedNotes="note.selectedNotes"
            v-model:isOpenModal="isOpenModal" 
            v-if="isOpenModal"
            :dentition="dentition"
            :dentitionType="dentitionType"
            @onAddNewTask="onAddNewTask"
        />
        <TaskModal
            v-if="store.state.isModalOpenFour"
            :selectedTask="selectedTask"
            @onSaveTask="onSaveTask"
            :editData="editData"
        />
    </div>
</template>

<script setup>
import {computed, ref} from "vue"
import { convertToTitleCase } from '@/services/utils/global';
import AddTreatmentPlan from './AddTreatmentPlan.vue';
import { formatDate } from '@/services/utils/global.js';
import {useRoute} from "vue-router";
import ServiceTask from './ServiceTask.vue';
import { useStore } from "vuex";
import TaskModal from './TaskModal.vue';

const props = defineProps({
    note: {
        type: Object,
        default: () => {}
    },
    index: {
        type: Number,
        default: -1,
    },
    salesOrderId: {
        type: Number || null,
        default: null,
    },
    generalId: {
        type: Number || null,
        default: null,
    },
    dentition: {
        type: Object || null,
        default: () => {}
    },
    dentitionType: {
        type: String || null,
        default: null,
    },
    clientId: {
        type: Number || null,
        default: null,
    }
})

const route = useRoute();
const generalId = computed(() => route.query.general_id);
const store = useStore();

const emit = defineEmits(['openModal']);
const isOpenModal = ref(false);
const selectedTask = ref(null);
const editData = ref(null);

const onOpenModal = (note, index = -1) => {
    if(note.value){
        note.name = convertToTitleCase(note.value);
    }
    
    isOpenModal.value = true
}

const onOpenEditModal = (item, index, childIndex, parentItem) => {
    editData.value = item;
    editData.value.index = index;
    editData.value.childIndex = childIndex;
    selectedTask.value = parentItem;
    selectedTask.value.client_id = props.clientId;
    store.state.isModalOpenFour = true;
}

const onSaveTask = (item) => {
    const noteItem = props.note.selectedNotes.find(note => note.id === item.activitylogable_id);
    if(! noteItem) return;

    const taskIndex = noteItem.items.findIndex(task => task.id === item.id);
    if(taskIndex !== -1) {
        noteItem.items[taskIndex] = item;
        return;
    }
    
    noteItem.items.push(item);
}

const onOpenTaskModal = (item) => {
    editData.value = null;
    selectedTask.value = item;
    selectedTask.value.client_id = props.clientId;
    store.state.isModalOpenFour = true;
}

const onAddNewTask = () => {
    store.state.dental.serviceRef && store.state.dental.serviceRef.submit(false, 'treatment_plan');
}

const getStatus = (status) => {
    if(! status) return '';
    if(status === 'pending') {
        return `<span class="badge bg-warning">Ongoing</span>`;
    } else if(status === 'completed') {
        return `<span class="badge bg-success"><i class="fas fa-check"></i> Completed</span>`;
    }
}

const getDentitionPertains = (dentitionPertains) => {
    if (!dentitionPertains || dentitionPertains.length < 0) return '';
    return dentitionPertains.filter(dentition => dentition.is_billed).map(dentition => dentition.tooth_number).join(', ').toUpperCase();
}

</script>

<style scoped>
/* ── Header ── */
.note-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
}
.note-title {
    min-width: 160px;
}
.note-title h4 {
    font-weight: 600;
    margin: 0;
    color: #374151;
}

/* ── Scroll wrapper ── */
.table-container {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}

/* ── Table base ── */
.treatment-table {
    table-layout: fixed;
    width: 100%;
    min-width: 820px;       /* scroll before crushing */
    border-collapse: separate;
    border-spacing: 0;
}

/* ── Header cells ── */
.treatment-table thead th {
    background-color: #f8f9fa;
    color: #6b7280;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.65rem 0.6rem;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ── Body cells ── */
.treatment-table tbody td {
    padding: 0.55rem 0.6rem;
    vertical-align: middle;
    border-bottom: 1px solid #f3f4f6;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #374151;
}

.treatment-table tbody tr:last-child td {
    border-bottom: none;
}

.treatment-table tbody tr:hover td {
    background-color: #f5f7ff;
}

/* ── Highlighted row (generalId match) ── */
.row-highlighted td {
    background-color: #bbf7d0 !important;
    color: #166534 !important;
}

/* ── Column widths ── */
.col-action    { width: 8%;  text-align: center; }
.col-item      { width: 20%; }
.col-doctor    { width: 13%; }
.col-schedule  { width: 14%; }
.col-dentition { width: 12%; }
.col-bill-dent { width: 14%; }
.col-amount    { width: 10%; }
.col-status    { width: 9%;  text-align: center; }

/* ── Task button ── */
.task-btn {
    padding: 0.2rem 0.5rem;
    white-space: nowrap;
}
</style>